<script lang="ts">
	import { t } from '$i18n/i18n'
	import { setContext } from 'svelte'
	import { fly } from 'svelte/transition'
	import Notifications from '$lib/organisms/Notifications.svelte'
	import AutocompleteInputField from '$lib/autocomplete/AutocompleteInputField.svelte'
	import { AutocompleteConfig, type AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'
	import type { ExtendedMaterial, Material } from './types/material'
	import TextArea, { textAreaHeights } from '$lib/atoms/TextArea.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import IconPlus from '$svg/icon-plus.svg?component'
	import { modals, onBeforeClose } from 'svelte-modals'
	import {
		SubstituteMaterialMapping,
		type SubstituteScriptImportData,
		type SubstituteType
	} from './types/substitutes'
	import MaterialSubstituteImport from './MaterialSubstituteImport.svelte'
	import { mmpvState } from './mmpv.state.svelte'
	import CollapsibleCard from '$lib/molecules/CollapsibleCard.svelte'
	import { getParentProperties } from './util/materialUtil'
	import type { Catalog } from './types/catalog'
	import { notifications } from '$lib/stores'
	import { saveScript } from './mmpv-substitutes.controller.svelte'

	type Props = {
		isOpen: boolean
		substituteType: SubstituteType
		catalogs?: Catalog[]
	}

	let { isOpen, substituteType, catalogs = [] }: Props = $props()

	const fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')
	const LOT_FORM_ERROR_PATH_PREFIX = 'lot.'
	setContext('useMaterialDesign', true)

	const materialAcRefConfig: AutocompleteFieldConfig = AutocompleteConfig('id', 'fullName')
	const materialAcCanConfig: AutocompleteFieldConfig = AutocompleteConfig('id', 'fullName')

	// material data variables
	let materialReferenceId: string | undefined = $state()
	let materialReferenceBackupId: string | undefined = $state()
	let materialReference: Material | undefined = $state()
	let materialCandidateId: string | undefined = $state()
	let materialCandidateBackupId: string | undefined = $state()
	let materialCandidate: Material | undefined = $state()
	let materialOptions: ExtendedMaterial[] = $derived([...mmpvState.allExtendedMaterials.values()])
	let substituteScriptImportData: SubstituteScriptImportData[] = $state([])
	// monotonic id source: array length would repeat identifiers after a removal
	let importIdentifierCounter = 0

	// unsaved changes overlay variables
	let allowSwitch: boolean = $state(true)
	let showOverlay: boolean = $state(false)
	const overlayTitle: string = $t('UI.mmpv.modal.substitute.overlay.title')
	const overlayMessage: string = $t('UI.mmpv.modal.substitute.overlay.message')

	// component variables
	let substituteHeaderComment: string = $state('')
	let scriptManualText: string = $state('')
	let scriptFullText: string = $derived(buildScriptText())
	let previewMode: boolean = $state(false)
	/**
	 * disables the preview toggle while there is nothing to preview: no import with a chosen
	 * property, no text in header comment and no manual script text.
	 */
	let disablePreview: boolean = $derived(
		!substituteScriptImportData.some((data) => data.defaultPropertyName) &&
		substituteHeaderComment?.length === 0 &&
		scriptManualText?.length === 0
	)

	// overlay actions of handling of unsaved changes

	/**
	 * Checks whether the actual substitute material has no imports.
	 *
	 * This function evaluates if the substitute material script import data is empty and if the changed material type
	 * has a substituteImportData value
	 *
	 * @return {boolean} - Returns true if there are no relevant material imports; otherwise, returns false.
	 */
	function actualSubstituteMaterialHasNoImports() {
		if (substituteScriptImportData.length === 0) {
			return true
		}
		if (materialReferenceBackupId !== materialReferenceId) {
			return !substituteScriptImportData.some(
				(data) =>
					data.materialType === SubstituteMaterialMapping.REFERENCE_MATERIAL &&
					data.defaultPropertyName
			)
		} else {
			return !substituteScriptImportData.some(
				(data) =>
					data.materialType === SubstituteMaterialMapping.CANDIDATE_MATERIAL &&
					data.defaultPropertyName
			)
		}
	}

	/**
	 * Decides whether the material is changed or not. If not, the material ids are reset to the selected material, as
	 * they have already been changed by the autocomplete component
	 *
	 * @param {boolean} allow - Indicates whether changes are allowed.
	 */
	function allowChanges(allow: boolean) {
		if (allow) {
			allowSwitch = true
			handleSwitch()
		} else {
			allowSwitch = false
			materialReferenceId = materialReferenceBackupId
			materialCandidateId = materialCandidateBackupId
		}
		showOverlay = false
	}

	/**
	 * Handles the switch operation by cloning material options based on their IDs,
	 * updating substitute script data, and resetting switch-related flag allowSwitch and backups ids.
	 *
	 */
	function handleSwitch() {
		/* $state.snapshot strips the Svelte 5 reactive proxy before cloning —
		 structuredClone cannot clone proxy objects and throws DataCloneError */
		materialReference = $state.snapshot(
			materialOptions.find((material) => material.id === materialReferenceId)
		)
		materialCandidate = $state.snapshot(
			materialOptions.find((material) => material.id === materialCandidateId)
		)
		updateSubstituteScriptImportData()
		materialReferenceBackupId = materialReferenceId
		materialCandidateBackupId = materialCandidateId
		allowSwitch = false
	}

	onBeforeClose(() => {
		// by closing modal remove existing notifications
		$notifications = $notifications.filter((notification) => notification.target === null)
	})

	function onMaterialChanged() {
		// switch material if boolean is true or if there are no imports else show overlay action
		if (allowSwitch || actualSubstituteMaterialHasNoImports()) {
			handleSwitch()
		} else {
			showOverlay = true
		}
	}

	/**
	 * Updates the `substituteScriptImportData` array based on the conditions related to `materialReferenceId`,
	 * `materialCandidateId`, `materialReferenceBackupId`, and `materialCandidateBackupId`. It filters the array
	 * to contain only the necessary items based on changes to reference and candidate IDs, and then applies
	 * the `updatePropertiesOfSubstituteScriptImport` function to each item in the updated array.
	 *
	 */
	function updateSubstituteScriptImportData() {
		if (!materialReferenceId && !materialCandidateId) {
			substituteScriptImportData = []
		} else if (materialReferenceBackupId !== materialReferenceId) {
			substituteScriptImportData = substituteScriptImportData.filter(
				(data) => data.materialType === SubstituteMaterialMapping.CANDIDATE_MATERIAL
			)
		} else if (materialCandidateBackupId !== materialCandidateId) {
			substituteScriptImportData = substituteScriptImportData.filter(
				(data) => data.materialType === SubstituteMaterialMapping.REFERENCE_MATERIAL
			)
		}
		substituteScriptImportData.forEach((data) => updatePropertiesOfSubstituteScriptImport(data))
	}

	function updatePropertiesOfSubstituteScriptImport(data: SubstituteScriptImportData) {
		let propertyIds: string[] = []

		if (materialReference?.propertyIds) {
			addAllProperties(materialReference, propertyIds)
		}
		if (materialCandidate?.propertyIds) {
			addAllProperties(materialCandidate, propertyIds)
		}

		data.properties = [...mmpvState.materialProperties.values()].filter((property) =>
			propertyIds.includes(property.id)
		)
	}

	function addAllProperties(material: Material, propertyIds: string[]) {
		if (material?.propertyIds) {
			let materialCatalog: Catalog | undefined
			for (const catalog of catalogs.values()) {
				if (catalog.nodes.some((node) => node.id === material.id)) {
					materialCatalog = catalog
				}
			}

			/* no catalog match (e.g. catalogs prop empty): skip only the inherited
			 properties — the material's own propertyIds below must still be collected */
			const inherited = materialCatalog ? getParentProperties(material, materialCatalog) : []
			;[...material.propertyIds, ...inherited].forEach((propId) => {
				if (!propertyIds.includes(propId)) {
					propertyIds.push(propId)
				}
			})
		}
	}

	function handleRemoveImport(identifier: number) {
		const indexOfImport = substituteScriptImportData
			.map((data) => data.identifier)
			.indexOf(identifier)

		if (indexOfImport > -1) {
			substituteScriptImportData.splice(indexOfImport, 1)
		}
	}

	function pushSubstituteImportData() {
		let data: SubstituteScriptImportData = {
			defaultPropertyName: '',
			ownPropertyName: '',
			properties: [],
			identifier: ++importIdentifierCounter,
			text: '',
			materialType: SubstituteMaterialMapping.REFERENCE_MATERIAL
		}
		// add all properties to data obj of selected material reference and candidate
		updatePropertiesOfSubstituteScriptImport(data)
		// add the new import to the start of the array to show as first object in ui
		substituteScriptImportData.unshift(data)
	}

	/**
	 * Constructs the complete script text by concatenating following objects: 1. header comments 2. imports and
	 * declares as variable substituteScriptImportData 3. script text.
	 *
	 * pure builder feeding the scriptFullText derived above — deep reads of substituteScriptImportData
	 * (defaultPropertyName, text) keep it up to date with mutations done by MaterialSubstituteImport.
	 */
	function buildScriptText(): string {
		let scriptText = ''

		if (substituteHeaderComment) {
			scriptText += substituteHeaderComment + '\n\n'
		}

		substituteScriptImportData.forEach((data) => {
			if (data.defaultPropertyName) {
				scriptText += data.text + '\n'
			}
		})

		if (scriptManualText) {
			scriptText += '\n' + scriptManualText
		}
		return scriptText
	}
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal"
		in:fly|global={{ y: -1000, duration: 400 }}
		out:fly|global={{ y: -1000, duration: 400 }}
	>
		<div
			class="contents"
			class:show-overlay={showOverlay}
			style="--content-padding: 3.13rem 3.5rem; max-width: 920px; width: 920px"
		>
			<Notifications target="modal" position="absolute" top="0" />
			{#if showOverlay}
				<div class="modal-overlay-content overlay-alignment">
					<div>
						<span>{@html overlayTitle}</span>
						<p>{@html overlayMessage}</p>
					</div>
					<div>
						<Button variant="secondary" color="red" onclick={() => allowChanges(false)}>
							{$t('Button.cancel')}
						</Button>

						<Button variant="primary" color="red" onclick={() => allowChanges(true)}>
							{$t('UI.mmpv.modal.substitute.overlay.button.confirm')}
						</Button>
					</div>
				</div>
			{/if}
			<div class="substitute-container">
				<div class:hidden={previewMode}>
					<span>{$t('UI.mmpv.modal.substitute.text.general.header')}</span>
					<span>{@html $t('UI.mmpv.modal.substitute.text.general.subheader')}</span>
					<div class="materials-autocomplete">
						<div>
							<AutocompleteInputField
								bind:value={materialReferenceId}
								label={$t('UI.mmpv.modal.substitute.autocomplete.label.reference')}
								autocompleteConfig={materialAcRefConfig}
								options={materialOptions}
								errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'material'}
								{fontProps}
								required
								onChange={onMaterialChanged}
							/>
						</div>
						<div>
							<AutocompleteInputField
								bind:value={materialCandidateId}
								label={$t('UI.mmpv.modal.substitute.autocomplete.label.candidate')}
								autocompleteConfig={materialAcCanConfig}
								options={materialOptions}
								errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'material'}
								{fontProps}
								required
								onChange={onMaterialChanged}
							/>
						</div>
					</div>
					<TextArea
						bind:text={substituteHeaderComment}
						title={$t('UI.mmpv.modal.substitute.script.header.title')}
						placeholder={$t('UI.mmpv.modal.substitute.script.header.placeholder')}
						titleInline
					/>
					<div class="imports-and-declares">
						<div>
							<span>{$t('UI.mmpv.modal.substitute.text.importsAndDeclares.header')}</span>
							<Button
								variant="secondary-icon"
								title={$t('UI.mmpv.modal.substitute.import.header')}
								disabled={!materialReferenceId && !materialCandidateId}
								onclick={pushSubstituteImportData}
							>
								<IconPlus width={20} height={20} fill="currentColor" />
							</Button>
						</div>
						{#each substituteScriptImportData as data, index (data.identifier)}
							<div style="padding: .5rem 0;">
								<CollapsibleCard openByDefault>
									<div slot="header" class="imports-and-declares-header">
										<h3>
											{$t('UI.mmpv.modal.substitute.import.card.header')}
											{data.ownPropertyName?.length > 0
												? data.ownPropertyName
												: data.defaultPropertyName
													? data.defaultPropertyName
													: ''}
										</h3>
									</div>
									<div slot="body">
										<!-- runes mode forbids binding to the each item itself, so bind via index -->
										<MaterialSubstituteImport
											bind:data={substituteScriptImportData[index]}
											{materialReference}
											{materialCandidate}
											onRemoveImport={handleRemoveImport}
										/>
									</div>
								</CollapsibleCard>
							</div>
						{/each}
					</div>
					<div class="script-text">
						<TextArea
							bind:text={scriptManualText}
							title={$t('UI.mmpv.modal.substitute.script.text.title')}
							marginTop={substituteScriptImportData?.length === 0 ? '.3rem' : '1rem'}
							placeholder={$t('UI.mmpv.modal.substitute.script.text.placeholder')}
							height={textAreaHeights.LARGE}
							titleInline
						/>
					</div>
				</div>
				<div class:hidden={!previewMode}>
					<span>{$t('UI.mmpv.modal.substitute.script.preview.title')}</span>
					<!-- no bind: scriptFullText is derived and the preview is readonly, so nothing flows back -->
					<TextArea
						text={scriptFullText}
						height="38rem"
						title={$t('UI.mmpv.modal.substitute.script.preview.inlineTitle')}
						placeholder={$t('UI.mmpv.modal.substitute.script.text.placeholder')}
						titleInline
						readonly
					/>
				</div>
				<div class="substitute-modal-footer">
					<div>
						<Button
							variant="secondary"
							color="green"
							disabled={disablePreview}
							width="5.25rem"
							onclick={() => (previewMode = !previewMode)}
						>
							{$t('UI.mmpv.modal.substitute.button.preview')}
						</Button>
					</div>
					<div>
						<Button
							variant="secondary"
							color="green"
							width="6.375rem"
							onclick={() => modals.close()}
						>
							{$t('Button.cancel')}
						</Button>
						<Button
							variant="primary"
							color="green"
							width="6.175rem"
							disabled={!materialReferenceId || !materialCandidateId}
							onclick={async () => {
								const ok = await saveScript({
									substituteTypePublicId: substituteType.publicId,
									refMatPublicId: materialReferenceId,
									canMatPublicId: materialCandidateId,
									dirrtyScript: scriptFullText
								})
								if (ok) modals.close()
							}}
						>
							{$t('UI.button.save')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
    @import '$styles/modals.scss';

    h2 {
        width: auto;
        margin: 0;
    }

    .hidden {
        display: none !important;
    }

    div.overlay-alignment {
        width: 100%;
        height: 93%;
        background: white;
        position: absolute;
        z-index: 99999999;
    }

    .contents {
        max-height: 90%;
    }

    .substitute-container {
        display: flex;
        flex-direction: column;
        align-items: normal;
        justify-content: normal;

        height: 45rem;
        width: 52rem;
        overflow: hidden scroll;
        scrollbar-width: none;
        padding: 0 2px;

        & > div {
            display: flex;
            flex-flow: column;
        }

        ::-webkit-scrollbar {
            display: none;
        }

        & :global(.green-text) {
            color: var(--primary-color);
        }

        & > div > span:first-child {
            font-family: Roboto, sans-serif;
            line-height: 24px;
            font-weight: 500;
            font-size: 1rem;
            color: black;

            padding-bottom: 0.5rem;
        }

        & > div > span:nth-child(2) {
            font-family: Roboto, sans-serif;
            line-height: 20px;
            font-weight: 400;
            font-size: 0.75rem;
            color: var(--color-slate-900);

            padding-bottom: 1rem;
        }

        & > div > div.materials-autocomplete {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            column-gap: 14px;

            & > div {
                width: 400px;
                max-width: 49%;
            }
        }

        div.imports-and-declares {
            padding-top: 0.5rem;

            & > div:first-child {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;

                position: sticky;
                top: -0.1rem;
                padding-bottom: 0.5rem;
                padding-top: 0.3rem;
                margin-left: -2px;
                margin-right: -2px;
                padding-right: 2px;
                min-height: 3rem;
                z-index: 1;
                background-color: white;

                & > span {
                    font-family: Roboto, sans-serif;
                    line-height: 18px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    color: var(--color-blue-500);

                    align-self: center;
                }
            }

            .imports-and-declares-header {
                font-family: Roboto, sans-serif;
                line-height: 20px;
                font-weight: 500;
                font-size: 0.75rem;
                color: var(--color-slate-900);

                padding-top: 1rem;
                padding-left: 1rem;
            }
        }

        div.script-text {
            padding-bottom: 2rem;
        }

        & > div.substitute-modal-footer {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;

            position: absolute;
            background: white;
            bottom: 0;
            height: 5rem;
            border: none;
            width: inherit;
            margin: 0 -2px;

            & > div:nth-child(2) {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                column-gap: 16px;
            }
        }
    }
</style>
